/**
 * HTTP CLIENT — Wrapper fetch con API axios-compatibile.
 *
 * Espone get/post/put/patch/delete con la stessa signature di axios
 * in modo che tutti gli *Api.ts del core e dei plugin restino invariati.
 * Ritorna sempre un oggetto { data } per compatibilita'.
 *
 * Interceptor:
 * - Request: aggiunge Authorization: Bearer <token> leggendo da tokenManager
 *   (memoria module-level, non localStorage — vedi AUTH_LOGIN_GUIDE.md)
 * - Response 401: prova UN refresh + retry via performRefresh() (race-safe
 *   con singleton in-flight condivisa col thunk authSlice.refreshAccessToken).
 *   Se il refresh fallisce o il retry torna ancora 401, clearTokens() +
 *   redirect a /login. Gli endpoint di auth (/user/login, /user/refresh,
 *   /user/resetPassword) sono esclusi dal loop per evitare ricorsione.
 *
 * Factory `createHttpClient(baseURL)`: crea un'istanza con baseURL
 * dedicato. `httpClient` e' l'istanza singleton del core (`CRM_BASE_URL`).
 * I plugin con un secondo backend (es. trentino-sicurezza su `/o/ts-crm/v1_0`)
 * usano `createHttpClient` per creare la propria istanza; `tokenManager` e
 * `performRefresh` restano shared (il refresh endpoint e' fisso sul core,
 * il retry riparte sull'URL assoluto originale quindi sopravvive cross-baseURL).
 *
 * Multipart: se `body instanceof FormData`, il client non imposta
 * `Content-Type` (browser lo genera con boundary) e passa FormData raw a
 * fetch. Senza questo fix gli upload multipart ricevono un Content-Type
 * `application/json` + body binario e falliscono lato server.
 *
 * In mock mode (VITE_USE_MOCKS=true), MSW (Mock Service Worker) intercetta
 * le chiamate a livello di rete tramite Service Worker. Vedi mockWorker.ts.
 */

import i18n from '../i18n';
import { CRM_BASE_URL } from './config';
import { performRefresh } from '../auth/refreshAuth';
import { clearTokens, getAccessToken, getApiHost } from '../auth/tokenManager';

// Mappa la lingua app (i18next: 'it' | 'en') al tag locale completo inviato in
// Accept-Language. Il tag completo (it-IT / en-US) toglie ogni ambiguita' al
// matching di AcceptLanguage.getPreferredLocale() lato Liferay contro i locale
// della company (it_IT, en_US). Fallback al codice grezzo per lingue non mappate.
const LOCALE_TAGS: Record<string, string> = { it: 'it-IT', en: 'en-US' };

function currentLocaleTag(): string {
  const lang = i18n.language || 'it';
  return LOCALE_TAGS[lang] ?? lang;
}

interface RequestConfig {
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  /** 'blob' per i download binari: la response viene letta con response.blob()
   *  invece di response.text()/JSON. Default (omesso) = JSON/testo. */
  responseType?: 'blob';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface HttpResponse<T = any> {
  data: T;
  status: number;
}

export interface HttpClient {
  defaults: { baseURL: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get<T = any>(url: string, config?: RequestConfig): Promise<HttpResponse<T>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<T = any>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put<T = any>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patch<T = any>(url: string, body?: unknown, config?: RequestConfig): Promise<HttpResponse<T>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete<T = any>(url: string, config?: RequestConfig): Promise<HttpResponse<T>>;
  /** GET che ritorna il body come Blob (download binari). Riusa auth Bearer
   *  e refresh 401 come gli altri metodi. */
  getBlob(url: string, config?: RequestConfig): Promise<HttpResponse<Blob>>;
}

class HttpError extends Error {
  response: { status: number; data: unknown };
  constructor(status: number, data: unknown, message: string) {
    super(message);
    this.response = { status, data };
  }
}

const AUTH_ENDPOINT_SUFFIXES = [
  '/user/login',
  '/user/refresh',
  '/user/resetPassword',
];

function isAuthEndpoint(url: string): boolean {
  const path = url.split('?')[0].replace(/\/$/, '');
  return AUTH_ENDPOINT_SUFFIXES.some((suffix) => path.endsWith(suffix));
}

function buildUrl(base: string, url: string, params?: Record<string, unknown>): string {
  // URL assoluto -> usa com'e'; relativo -> prepone baseURL
  let full = /^https?:\/\//i.test(url) ? url : `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
  // Setup multi-tenant: se l'URL e' assoluto (VITE_CRM_BASE_URL valorizzato con
  // URL assoluto, es. http://localhost:8080/o/crm/v1_0 in locale o
  // https://crm-api.../o/crm/v1_0 in build) e c'e' un apiHost, riscrivi SOLO
  // l'hostname preservando schema/porta/path/query — il Bearer e' company-scoped
  // e Liferay 401-a se l'host risolve a una company diversa.
  //
  // URL relativi: nessuna rewrite. Il browser invia all'origin della SPA: utile
  // solo in setup single-tenant via proxy Vite (apiHost = host della SPA, quindi
  // riscrivere sarebbe no-op). Per ambienti multi-tenant valorizzare
  // VITE_CRM_BASE_URL con URL assoluto (gia' fatto in .env.local,
  // .env.jenkins-svil, .env.jenkins-prod).
  const apiHost = getApiHost();
  if (apiHost && /^https?:\/\//i.test(full)) {
    try {
      const parsed = new URL(full);
      parsed.hostname = apiHost;
      full = parsed.toString();
    } catch { /* URL non parseable: lascia stare */ }
  }
  if (!params) return full;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    qs.append(k, String(v));
  }
  const q = qs.toString();
  if (!q) return full;
  return full.includes('?') ? `${full}&${q}` : `${full}?${q}`;
}

async function request<T>(
  client: HttpClient,
  method: string,
  url: string,
  body: unknown,
  config?: RequestConfig,
): Promise<HttpResponse<T>> {
  const finalUrl = buildUrl(client.defaults.baseURL, url, config?.params);

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const isBlob = config?.responseType === 'blob';

  // FormData: NO Content-Type (il browser genera il boundary).
  // JSON: Content-Type applicato di default; override da config?.headers possibile.
  // Accept-Language: derivato dalla lingua app (i18next), NON dal default del
  // browser — senza questo header il backend traduce sui locale del browser
  // (it in dev, en in prod) ignorando la lingua scelta dall'utente. Override da
  // config?.headers ancora possibile (spread successivo).
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'Accept-Language': currentLocaleTag(),
    ...(config?.headers ?? {}),
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const init: RequestInit = { method, headers };
  if (body !== undefined && body !== null) {
    init.body = isFormData
      ? (body as FormData)
      : typeof body === 'string'
        ? body
        : JSON.stringify(body);
  }

  let response = await fetch(finalUrl, init);

  // 401 refresh loop: tenta UN refresh + retry, solo su endpoint non-auth.
  // performRefresh e' race-safe: chiamate concorrenti convergono sulla stessa
  // singleton promise condivisa col thunk authSlice.refreshAccessToken.
  if (response.status === 401 && !isAuthEndpoint(finalUrl)) {
    try {
      await performRefresh();
      // Costruisce un init separato con il nuovo token da tokenManager
      // (source of truth). Evita la dipendenza implicita sulla mutazione
      // dell'oggetto headers condiviso con il primo init.
      const retryInit: RequestInit = {
        ...init,
        headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
      };
      response = await fetch(finalUrl, retryInit);
    } catch {
      // refresh fallito: il codice sotto pulisce tokens e redirige a /login
    }
  }

  if (response.status === 401) {
    clearTokens();
    // In mock mode evitiamo hard navigation: non serve (i mock rispondono 200)
    // e disturba MSW che proverebbe a intercettare la GET /login.
    if (typeof window !== 'undefined' && import.meta.env.VITE_USE_MOCKS !== 'true') {
      window.location.href = '/login';
    }
  }

  // Su errore il body è sempre testo/JSON (messaggio di errore), anche per le
  // richieste blob: un 4xx/5xx è una response di errore, non il file richiesto.
  if (!response.ok) {
    let data: unknown = null;
    const text = await response.text();
    if (text) {
      try { data = JSON.parse(text); } catch { data = text; }
    }
    throw new HttpError(response.status, data, `Request failed with status ${response.status}`);
  }

  // Successo: blob per i download binari, altrimenti JSON/testo (o vuoto).
  if (isBlob) {
    return { data: (await response.blob()) as T, status: response.status };
  }

  let data: unknown = null;
  const text = await response.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }

  return { data: data as T, status: response.status };
}

/**
 * Crea una nuova istanza HttpClient con il baseURL specificato. `tokenManager`,
 * `performRefresh` e l'handling 401 sono shared tra tutte le istanze: un login
 * vale per qualsiasi client, e un refresh avviato da un client sblocca anche
 * gli altri.
 */
export function createHttpClient(baseURL: string): HttpClient {
  const client: HttpClient = {
    defaults: { baseURL },
    get: <T>(url: string, config?: RequestConfig) =>
      request<T>(client, 'GET', url, undefined, config),
    post: <T>(url: string, body?: unknown, config?: RequestConfig) =>
      request<T>(client, 'POST', url, body, config),
    put: <T>(url: string, body?: unknown, config?: RequestConfig) =>
      request<T>(client, 'PUT', url, body, config),
    patch: <T>(url: string, body?: unknown, config?: RequestConfig) =>
      request<T>(client, 'PATCH', url, body, config),
    delete: <T>(url: string, config?: RequestConfig) =>
      request<T>(client, 'DELETE', url, undefined, config),
    getBlob: (url: string, config?: RequestConfig) =>
      request<Blob>(client, 'GET', url, undefined, { ...config, responseType: 'blob' }),
  };
  return client;
}

/** Istanza singleton del core (`/o/crm/v1_0` di default). */
export const httpClient: HttpClient = createHttpClient(CRM_BASE_URL);
